// Centralised profile API - exposes both the legacy (saveProfileData /
// uploadProfileImage) and the Grahak (PATCH /user, S3 upload + PATCH /user)
// implementations behind the same input/output contract so they can be selected
// by `AuthAPI` via `pick(legacy, grahak)` in `authResolver.ts`.

// helpers
import { getUrl, getGrahakUrl, ROUTES } from 'shared/api/urls';
import { ApiHelper } from 'shared/helpers/api';
import { updateUser } from 'shared/api/user';
import { log } from 'shared/helpers/newRelic';
import { trackGrahakFunctionality } from 'shared/helpers/grahakMonitoring';
import type { ProfileUser } from 'shared/api/profileHelpers';
import {
  getGrahakGender,
  normalizeGrahakUser,
  splitName,
  toGrahakDob,
  toLegacyDob,
} from 'shared/api/profileHelpers';

// constants
import { TAG_IDENTIFIER } from 'shared/helpers/newRelic/constants';

const MODULE_IDENTIFIER = 'My Profile';

/* -------------------------------------------------------------------------- */
/*  Shared contracts                                                          */
/* -------------------------------------------------------------------------- */

export interface SaveProfilePayload {
  name: string;
  dob: string;
  gender: string;
  imageId?: string;
}

export interface SaveProfileResult {
  status: 'success' | 'fail';
  message: string;
  response?: any;
}

export interface UploadProfilePicResult {
  /** Either a base64 data string (legacy) or a hosted image URL (Grahak). */
  image: string;
  /** Only populated on the Grahak path. */
  userDetails?: any;
  message: string;
}

/**
 * Common `user` shape the app consumes. Legacy and Grahak fetchers both
 * project their respective payloads onto this. `imageLink` is populated
 * directly by the Grahak path (no second request needed); for the legacy
 * path, `image_url` carries the old `/getProfileImage?id=...` URL.
 */
export type { ProfileUser } from 'shared/api/profileHelpers';

export interface GetProfileDataResult {
  status: string;
  response: {
    user: ProfileUser;
    [key: string]: any;
  };
}

/* -------------------------------------------------------------------------- */
/*  Legacy — saveProfileData                                                  */
/* -------------------------------------------------------------------------- */

export const saveProfileDataLegacy = async (
  profileData: SaveProfilePayload
): Promise<SaveProfileResult> => {
  const url = getUrl(ROUTES.SAVE_MY_PROFILE);
  const dob = toLegacyDob(profileData.dob);
  const body = { dob, gender: profileData.gender, name: profileData.name };

  const response = await ApiHelper(url, { method: 'POST', body, isBodyJSON: true });
  if (!response.ok) {
    return Promise.reject(response);
  }
  return response.json();
};

/* -------------------------------------------------------------------------- */
/*  Grahak — saveProfileData                                                  */
/* -------------------------------------------------------------------------- */

export const saveProfileDataGrahak = async (
  profileData: SaveProfilePayload
): Promise<SaveProfileResult> => {
  const {
    name = '',
    dob = '',
    gender = '',
    imageId,
  } = profileData;
  const { firstName, lastName } = splitName(name);
  const grahakGender = getGrahakGender(gender);

  const payload: {
    firstName: string;
    lastName: string;
    gender?: string;
    dob: string;
    imageId?: string;
  } = {
    firstName,
    lastName,
    dob: toGrahakDob(dob),
  };
  if (grahakGender) payload.gender = grahakGender;
  if (imageId) payload.imageId = imageId;

  const result = await updateUser(payload);

  if (result.success) {
    return {
      status: 'success',
      message: result.data?.message || 'Profile updated successfully',
      response: result.data?.data?.userDetails,
    };
  }

  return Promise.reject({
    status: 'fail',
    message: result.message || 'Failed to update profile',
  });
};

/* -------------------------------------------------------------------------- */
/*  Legacy — uploadProfilePic                                                 */
/* -------------------------------------------------------------------------- */

const toBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

/**
 * Reads a File and returns the RAW base64 string (without the
 * `data:image/...;base64,` prefix). Used by the Grahak upload adapter so the
 * value can be dropped straight into the consumer's `data:${fmt};base64,${x}`
 * template without double-prefixing.
 */
const toBase64Raw = async (file: File): Promise<string> => {
  const dataUrl = await toBase64(file);
  const commaIdx = dataUrl.indexOf(',');
  return commaIdx === -1 ? dataUrl : dataUrl.slice(commaIdx + 1);
};

const uploadProfilePicLegacyRaw = (fileName: string, fileData: string) => {
  const url = getUrl(ROUTES.UPDATE_PROFILE_PIC);
  const body = { image_data: { fileName, fileData } };

  return ApiHelper(url, { method: 'POST', body, isBodyJSON: true }).then((response: Response) => {
    if (!response.ok) return Promise.reject(response);
    return response.json();
  });
};

const fetchProfileImage = (imageId: string): Promise<string> => {
  const baseUrl = getUrl(ROUTES.GET_PROFILE_IMAGE);
  return ApiHelper(`${baseUrl}?id=${imageId}`)
    .then((response: Response) => {
      if (!response.ok) return Promise.reject(response);
      return response.json();
    })
    .then((res) => res.response.image_data);
};

export const uploadProfilePicLegacy = async (file: File): Promise<UploadProfilePicResult> => {
  const base64 = await toBase64(file);
  const uploadRes = await uploadProfilePicLegacyRaw(file.name || 'profile-image.jpg', base64);
  const imageId = uploadRes?.response?.id;
  const image = imageId ? await fetchProfileImage(imageId) : base64;

  return {
    image,
    message: uploadRes?.message || 'Profile picture uploaded successfully',
  };
};

/* -------------------------------------------------------------------------- */
/*  Grahak — uploadProfilePic (pre-signed S3 + PATCH /user)                   */
/* -------------------------------------------------------------------------- */

export const generateUploadLink = (fileName: string, fileSizeInBytes: number) => {
  const url = getGrahakUrl(ROUTES.GENERATE_UPLOAD_LINK);
  const body = {
    documentPurpose: 'profile',
    documentType: '',
    documentName: fileName,
    contentLengthInBytes: fileSizeInBytes,
  };

  return ApiHelper(url, { method: 'POST', body, isBodyJSON: true }).then((response: Response) => {
    if (!response.ok) return Promise.reject(response);
    return response.json();
  });
};

export const uploadToS3 = (presignedUrl: string, file: File) =>
  fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
      'Content-Length': file.size.toString(),
    },
  }).then((response: Response) => {
    if (!response.ok) return Promise.reject(response);
    return response;
  });

export const uploadProfilePicGrahak = async (file: File): Promise<UploadProfilePicResult> => {
  const uploadLinkResponse = await generateUploadLink(file.name, file.size);

  if (uploadLinkResponse?.status !== 'success') {
    throw new Error(uploadLinkResponse?.message || 'Failed to generate upload link');
  }

  const { documentId, uploadLink } = uploadLinkResponse.data || {};

  // Guard against a malformed/empty `data` payload — without `uploadLink` or `documentId`
  if (!uploadLink || !documentId) {
    throw new Error(
      uploadLinkResponse?.message ||
        'Invalid upload link response: missing uploadLink or documentId'
    );
  }

  await uploadToS3(uploadLink, file);

  const updateUserResponse = await updateUser({ imageId: documentId });

  if (!updateUserResponse.success) {
    throw new Error(updateUserResponse.message || 'Failed to update user profile');
  }

  // Response shape: { data: { data: { userDetails: { imageLink, ... } } } }
  const userDetails = updateUserResponse.data?.data?.userDetails;

  // Return base64 (NOT the hosted imageLink) for the `image` field so the
  // existing consumer template `data:${fmt};base64,${image}` keeps working
  // on the Grahak path. `userDetails.imageLink` is still exposed for any
  // caller that needs the hosted URL (e.g. to sync Redux customer state).
  // We already have the File in hand — no extra network round trip.
  const imageBase64 = await toBase64Raw(file);

  return {
    image: imageBase64,
    userDetails,
    message: updateUserResponse.data?.message || 'Profile picture updated successfully',
  };
};

/* -------------------------------------------------------------------------- */
/*  getProfileData — legacy & Grahak                                          */
/* -------------------------------------------------------------------------- */

export const getProfileDataLegacy = async (): Promise<GetProfileDataResult> => {
  const url = getUrl(ROUTES.GET_MY_PROFILE);
  const response = await ApiHelper(url);
  if (!response.ok) return Promise.reject(response);
  return response.json();
};

/**
 * Maps Grahak's `userDetails` payload onto the legacy `getProfileData` shape
 * so callers can stay agnostic. The crucial bit: `user.imageLink` is the
 * resolved image URL — no second request needed.
 *
 * Grahak gender enum: 1 = MALE, 2 = FEMALE, 3 = OTHERS (per backend).
 */
export const getProfileDataGrahak = async (): Promise<GetProfileDataResult> => {
  const url = getGrahakUrl(ROUTES.UPDATE_USER); // GET /grahak-service/api/v1/user
  const response = await ApiHelper(url, { method: 'GET' });

  if (!response.ok) return Promise.reject(response);

  const json = await response.json();
  const userDetails = json?.data?.userDetails || {};
  const status = typeof json?.status === 'string' ? json.status.toLowerCase() : 'fail';

  return {
    status,
    response: {
      user: normalizeGrahakUser(userDetails),
    },
  };
};

/* -------------------------------------------------------------------------- */
/*  getProfileImage — legacy & Grahak                                         */
/* -------------------------------------------------------------------------- */

const fetchLegacyProfileImage = (imageId: string): Promise<string> => {
  const baseUrl = getUrl(ROUTES.GET_PROFILE_IMAGE);
  return ApiHelper(`${baseUrl}?id=${imageId}`)
    .then((response: Response) => {
      if (!response.ok) return Promise.reject(response);
      return response.json();
    })
    .then((res) => res?.response?.image_data || '');
};

const extractLegacyImageId = (image_url?: string): string => {
  if (!image_url) return '';
  try {
    return new URL(image_url).searchParams.get('id') || '';
  } catch {
    return '';
  }
};

/**
 * Unified profile-image resolver. Accepts the `user` object returned by
 * `AuthAPI.getProfileData()`. Legacy fetches `/getProfileImage?id=...`;
 * Grahak returns `user.imageLink` directly (no extra request).
 */
export const getProfileImageLegacy = async (user?: ProfileUser): Promise<string> => {
  const imageId = extractLegacyImageId(user?.image_url);
  if (!imageId) return '';
  try {
    return await fetchLegacyProfileImage(imageId);
  } catch (error) {
    log({
      errTitle: 'Error in getting profile img (legacy)',
      error,
      tags: { [TAG_IDENTIFIER.MODULE]: MODULE_IDENTIFIER },
    });
    return '';
  }
};

/**
 * FileReader → base64 helper. Strips the `data:*;base64,` prefix so the
 * value drops straight into the consumer template
 * `data:${imageFormat};base64,${imageData}` without double-prefixing.
 */
const blobToBase64Raw = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const commaIdx = dataUrl.indexOf(',');
      resolve(commaIdx === -1 ? dataUrl : dataUrl.slice(commaIdx + 1));
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(blob);
  });

/**
 * Grahak resolver. Backend stores the avatar on S3 and hands us a hosted
 * URL via `user.imageLink`, but existing consumers render through the legacy
 * `data:${fmt};base64,${imageData}` template — handing them a URL would
 * produce a broken image. Fetch the asset and normalize to base64 so the
 * Grahak path is contract-compatible with the legacy one.
 *
 * `fetch` is used directly (NOT `ApiHelper`) because:
 *   - The URL is a third-party S3 host; we don't want our default headers
 *     (`x-csrf-token`, `n-access-enabled`, etc.) leaking to S3.
 *   - No retry/refresh interception is appropriate for a pre-signed asset.
 *
 * Falls back to `''` on any failure so the consumer's `imageData ? ... :
 * branch keeps working.
 */
export const getProfileImageGrahak = async (user?: ProfileUser): Promise<string> => {
  const url = user?.imageLink;
  if (!url) return '';
  try {
    const response = await fetch(url);
    if (!response.ok) {
      trackGrahakFunctionality({
        functionality: 'get_profile_image',
        outcome: 'failure',
        reason: 'image_fetch_http_failure',
        statusCode: response.status,
      });
      return '';
    }
    const blob = await response.blob();
    return await blobToBase64Raw(blob);
  } catch (error) {
    trackGrahakFunctionality({
      functionality: 'get_profile_image',
      outcome: 'failure',
      reason: 'image_fetch_failed',
      error,
    });
    log({
      errTitle: 'Error in getting profile img (grahak)',
      error,
      tags: { [TAG_IDENTIFIER.MODULE]: MODULE_IDENTIFIER },
    });
    return '';
  }
};
